import React from 'react';
import { motion } from 'framer-motion';
import aboutUsImage from '../assets/A3-Creative-Hub-Business-Card.png'; // Use your actual logo image
import { faInstagram, faYoutube } from '@fortawesome/free-brands-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

export default function About() {
  const imageVariants = {
    hidden: { x: -100, opacity: 0, scale: 0.8 }, // Start further left, faded, slightly smaller
    visible: { x: 0, opacity: 1, scale: 1, transition: { duration: 0.5, ease: "easeOut" } },
  };

  const textVariants = { // These variants will apply to the combined 'About Us', 'Our Services', 'Our Location', and 'Open Hours' text sections
    hidden: { x: 100, opacity: 0, scale: 0.8 }, // Start further right, faded, slightly smaller
    visible: { x: 0, opacity: 1, scale: 1, transition: { duration: 0.5, ease: "easeOut"} }, // Added delay
  };

  // The buttonVariants constant is removed as the 'LEARN MORE' button is no longer used in this section.

  return (
    <section 
      id="about" 
      className="py-16 bg-gradient-to-r from-pink-200 via-yellow-100 to-pink-100 scroll-mt-16" 
    >
      {/* Main Container for the entire combined card (About Us + Our Services + Location + Hours) */}
      <div className="max-w-screen-xl mx-auto px-4 md:px-4 lg:px-12 items-start
                      bg-gradient-to-r from-pink-200 via-yellow-100 to-pink-100 rounded-lg shadow-lg overflow-hidden"> {/* Single card background, shadow, and rounded corners */}
        
        {/* Top Part: Left Image Section and Right Content Column */}
        <div className="flex flex-col lg:flex-row gap-8 lg:gap-0 
                        items-center lg:items-stretch"> 
          
          {/* Animated Left Section (Pink/Purple Background with Logo and Text) */}
          <motion.div
            className="lg:w-1/2 flex-shrink-0 relative flex flex-col p-8 lg:p-12
                       " /* Specific background & rounded corners */
            variants={imageVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }} 
          >
            <img
              src={aboutUsImage} 
              alt="A3 Creative Hub Logo"
              className="relative inset-0 w-full max-w-md mx-auto h-[full] object-cover rounded-l-lg lg:rounded translate-x-[-5%]" /* Centered, fixed size logo */
            />
          </motion.div>

          {/* Animated Right Section (White Background with All Content) */}
          <motion.div
            className="lg:w-1/2 p-8 md:p-12 lg:p-16 flex flex-col justify-center text-center lg:text-left
                       rounded-r-lg lg:rounded-l-none" 
            variants={textVariants} 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }} 
          >
            {/* About Us Content */}
            <h2 className="text-4xl lg:text-4xl font-extrabold text-blue-gray-800 mb-6">
              About Us
            </h2>
            <p className="text-gray-700 leading-relaxed text-xl mb-8">
              A3 Creative Hub is your go-to for affordable, artistic, and accessible creative return gift ideas. We specialize in bringing your ideas to life with passion and a local touch. From crafting unique return gifts that leave a lasting impression to offering a wide array of creative services, we pour our heart and soul into every customer. We believe in understanding your needs deeply and collaborating closely to deliver results that exceed your expectations.
            </p>
            
            {/* Our Services Content */}
            <h2 className="text-4xl lg:text-4xl font-extrabold text-blue-gray-800 mb-6 mt-8"> {/* Added mt-8 for spacing */}
              Our Services
            </h2>
            <ul className="text-gray-700 leading-relaxed text-lg mb-6 pl-6">
              {[
                "ENGAGEMENT. WEDDING. SEEMANTHAM",
                "NAMING CEREMONY. AYUSHOMAM. BIRTHDAY PARTY. UPANAYANAM. PUBERTY",
                "25TH & 50TH ANNIVERSARY",
                "SHASHTIABTHAPOORTHI. BHEEMARADHASHANTHI. SATHABHISHEKAM",
                "VARALAKSHMI NOMBU. NAVRATRI",
                "HOUSE WARMING. CORPORATE EVENTS"
              ].map((service, index) => (
                <li key={index} className="flex items-start gap-2">
                  <span className="text-black mt-1">▲</span>
                  <span className="flex-1">{service}</span>
                </li>
              ))}
            </ul>

            {/* New: Our Location Details */}
            <h2 className="text-4xl  font-extrabold text-blue-gray-800 mb-6 mt-8"> {/* Added mt-8 for spacing */}
              Location
            </h2>
            <p className="text-gray-700 leading-relaxed font-bold text-lg mb-4">
              No:7/3, Veda Villas, Baroda Street 1st Lane, West Mambalam, Chennai, Tamil Nadu 600033.
            </p>
            <div className="w-full h-64 bg-gray-200 rounded-lg overflow-hidden mb-8">
            <iframe src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3886.8174035329193!2d80.20833557588793!3d13.047291913212643!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3a52674fe16a74c1%3A0x57acbbab5f8c1c90!2sA3%20Creative%20Hub!5e0!3m2!1sen!2sin!4v1748083883497!5m2!1sen!2sin" 
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Our Location on Map">
            </iframe>
            </div>

            {/* New: Shop Open Hours */}
            <h2 className="text-4xl  font-extrabold text-blue-gray-800 mb-6 mt-8"> {/* Added mt-8 for spacing */}
              Open Hours
            </h2>
            <p className="text-gray-700 leading-relaxed text-lg">
              <strong>Monday - Saturday: 10:00 AM - 10:00 PM <br/>
              Sunday Holiday 
              </strong>
            </p>
            <p className="text-md font-bold text-blue-gray-800 mt-8">
              Follow us on social media:
              <a href="https://www.instagram.com/_a3._official/" target="_blank" rel="noopener noreferrer" className="text-pink-600 hover:text-pink-900 font-semibold mx-1">
                <FontAwesomeIcon icon={faInstagram} size="2xl" /> {/* Instagram Logo */}
              </a>
              <a href="https://www.youtube.com/@A3CreativeHub-624" target="_blank" rel="noopener noreferrer" className="text-red-600 hover:text-red-800 font-semibold mx-1">
                <FontAwesomeIcon icon={faYoutube} size="2xl" /> {/* Facebook Logo */}
              </a>
            </p>
          </motion.div>
        </div> {/* End of Top Part: About Us two-column wrapper */}
      </div> {/* End of Main Container for the entire combined card */}
    </section>
  );
}